/** Animated score ring with gradient arc and count-up percentage. */
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';

const EASE = [0.16, 1, 0.3, 1] as const;

function useCountUp(target: number, duration: number, delay: number): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let frameId = 0;
    let cancelled = false;

    const delayTimer = setTimeout(() => {
      const startTime = performance.now();

      function tick() {
        if (cancelled) return;
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / (duration * 1000), 1);
        // easeOut curve
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(eased * target));

        if (progress < 1) {
          frameId = requestAnimationFrame(tick);
        }
      }

      frameId = requestAnimationFrame(tick);
    }, delay * 1000);

    return () => {
      cancelled = true;
      clearTimeout(delayTimer);
      cancelAnimationFrame(frameId);
    };
  }, [target, duration, delay]);

  return value;
}

export function ScoreRing({ scorePct }: { scorePct: number }) {
  const size = 160;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (scorePct / 100) * circumference;
  const displayPct = useCountUp(Math.round(scorePct), 1, 0.3);

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--f-border)"
          strokeWidth={strokeWidth}
        />
        {/* Gradient definition */}
        <defs>
          <linearGradient id="score-ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FF9B4A" />
            <stop offset="55%" stopColor="#FF6EB1" />
            <stop offset="100%" stopColor="#8A6CF0" />
          </linearGradient>
        </defs>
        {/* Progress arc */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#score-ring-gradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, delay: 0.3, ease: EASE }}
        />
      </svg>
      {/* Centre text — gradient earned at completion */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="f-gradient-text font-bold"
          style={{
            fontSize: 'var(--f-text-4xl)',
            fontFamily: 'var(--f-font-display)',
            letterSpacing: 'var(--f-tracking-tight)',
          }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.3 }}
        >
          {displayPct}%
        </motion.span>
      </div>
    </div>
  );
}
